import {
  collection,
  doc,
  setDoc,
  getDoc,
  getDocs,
  addDoc,
  updateDoc,
  deleteDoc,
  query,
  where,
  orderBy,
  limit,
  arrayUnion,
  arrayRemove,
  increment,
  runTransaction,
} from 'firebase/firestore';
import { ref, uploadBytesResumable, getDownloadURL, deleteObject } from 'firebase/storage';
import { db, storage } from '../config/firebase';

// Best-effort Storage cleanup for a download URL. Skips anything that isn't
// actually hosted in this project's Storage bucket (e.g. Pexels-sourced card
// images), and never throws — a missing/already-deleted file, or a card that
// never had a real upload, shouldn't block whatever delete flow called this.
async function deleteStorageUrlIfOwned(url) {
  if (!url || typeof url !== 'string' || !url.includes('firebasestorage.googleapis.com')) return;
  try {
    await deleteObject(ref(storage, url));
  } catch {
    // ignore — best effort
  }
}

// ============== CUSTOM SETS ==============

export const createCustomSet = async (userId, clinicId, setData) => {
  try {
    const customSetsRef = collection(db, 'customSets');
    const docRef = await addDoc(customSetsRef, {
      userId,
      clinicId,
      ...setData,
      createdAt: new Date(),
      updatedAt: new Date(),
      cardCount: 0,
    });
    return { success: true, id: docRef.id };
  } catch (error) {
    return { success: false, error: error.message };
  }
};

export const getCustomSet = async (setId) => {
  try {
    const setDoc_ = await getDoc(doc(db, 'customSets', setId));
    if (setDoc_.exists()) {
      return { success: true, data: { id: setId, ...setDoc_.data() } };
    }
    return { success: false, error: 'Set not found' };
  } catch (error) {
    return { success: false, error: error.message };
  }
};

export const getUserCustomSets = async (userId) => {
  try {
    const q = query(
      collection(db, 'customSets'),
      where('userId', '==', userId),
      orderBy('updatedAt', 'desc')
    );
    const querySnapshot = await getDocs(q);
    const sets = querySnapshot.docs.map((doc) => ({ id: doc.id, ...doc.data() }));
    return { success: true, data: sets };
  } catch (error) {
    return { success: false, error: error.message };
  }
};

export const updateCustomSet = async (setId, updates) => {
  try {
    await updateDoc(doc(db, 'customSets', setId), {
      ...updates,
      updatedAt: new Date(),
    });
    return { success: true };
  } catch (error) {
    return { success: false, error: error.message };
  }
};

export const deleteCustomSet = async (setId) => {
  try {
    // Delete all cards in subcollection first
    const cardsSnapshot = await getDocs(collection(db, 'customSets', setId, 'cards'));
    for (const card of cardsSnapshot.docs) {
      await deleteDoc(card.ref);
    }
    // Delete the set document
    await deleteDoc(doc(db, 'customSets', setId));
    return { success: true };
  } catch (error) {
    return { success: false, error: error.message };
  }
};

// ============== CARDS ==============

export const addCardToSet = async (setId, isPreMade, cardData) => {
  try {
    const collectionName = isPreMade ? 'preMadeSets' : 'customSets';
    const setRef  = doc(db, collectionName, setId);
    const cardRef = doc(collection(db, collectionName, setId, 'cards'));

    // Transaction so the card doc and the parent's cardCount either both
    // commit or neither does — a plain addDoc + separate updateDoc could
    // leave cardCount permanently behind the real subcollection size if the
    // second call failed (connection drop, tab closed) after the first succeeded.
    await runTransaction(db, async (transaction) => {
      transaction.set(cardRef, { ...cardData, createdAt: new Date() });
      transaction.update(setRef, { cardCount: increment(1) });
    });

    return { success: true, id: cardRef.id };
  } catch (error) {
    return { success: false, error: error.message };
  }
};

export const getSetCards = async (setId, isPreMade = false) => {
  try {
    const collectionName = isPreMade ? 'preMadeSets' : 'customSets';
    const q = query(
      collection(db, collectionName, setId, 'cards'),
      orderBy('order', 'asc')
    );
    const querySnapshot = await getDocs(q);
    const cards = querySnapshot.docs.map((doc) => ({ id: doc.id, ...doc.data() }));
    return { success: true, data: cards };
  } catch (error) {
    return { success: false, error: error.message };
  }
};

export const updateCard = async (setId, cardId, isPreMade, updates) => {
  try {
    const collectionName = isPreMade ? 'preMadeSets' : 'customSets';
    await updateDoc(
      doc(db, collectionName, setId, 'cards', cardId),
      updates
    );
    return { success: true };
  } catch (error) {
    return { success: false, error: error.message };
  }
};

export const deleteCard = async (setId, cardId, isPreMade) => {
  try {
    const collectionName = isPreMade ? 'preMadeSets' : 'customSets';
    const setRef  = doc(db, collectionName, setId);
    const cardRef = doc(db, collectionName, setId, 'cards', cardId);

    await runTransaction(db, async (transaction) => {
      transaction.delete(cardRef);
      transaction.update(setRef, { cardCount: increment(-1) });
    });

    return { success: true };
  } catch (error) {
    return { success: false, error: error.message };
  }
};

// ============== IMAGE UPLOAD ==============

export const uploadCardImage = async (userId, setId, cardId, imageFile, onProgress) => {
  try {
    const fileName = `${Date.now()}_${imageFile.name}`;
    const storageRef = ref(storage, `users/${userId}/uploads/images/${setId}/${cardId}/${fileName}`);
    await new Promise((resolve, reject) => {
      const task = uploadBytesResumable(storageRef, imageFile);
      task.on('state_changed',
        (snap) => onProgress && onProgress(Math.round((snap.bytesTransferred / snap.totalBytes) * 100)),
        reject,
        resolve,
      );
    });
    const downloadURL = await getDownloadURL(storageRef);
    return { success: true, url: downloadURL, path: storageRef.fullPath };
  } catch (error) {
    return { success: false, error: error.message };
  }
};

export const deleteCardImage = async (imagePath) => {
  try {
    const storageRef = ref(storage, imagePath);
    await deleteObject(storageRef);
    return { success: true };
  } catch (error) {
    return { success: false, error: error.message };
  }
};

// ============== DECKS ==============

export const createDeck = async (userId, clinicId, deckData) => {
  try {
    const deckRef = await addDoc(collection(db, 'decks'), {
      userId,
      clinicId,
      ...deckData,
      createdAt: new Date(),
      updatedAt: new Date(),
    });
    return { success: true, id: deckRef.id };
  } catch (error) {
    return { success: false, error: error.message };
  }
};

export const getUserDecks = async (userId) => {
  try {
    const q = query(
      collection(db, 'decks'),
      where('userId', '==', userId),
      orderBy('updatedAt', 'desc')
    );
    const querySnapshot = await getDocs(q);
    const decks = querySnapshot.docs.map((doc) => ({ id: doc.id, ...doc.data() }));
    return { success: true, data: decks };
  } catch (error) {
    return { success: false, error: error.message };
  }
};

// ============== SESSIONS ==============

export const createSession = async (userId, clientId, clinicId, deckId, sessionData) => {
  try {
    const sessionRef = await addDoc(collection(db, 'sessions'), {
      userId,
      clientId,
      clinicId,
      deckId,
      status: 'in-progress',
      trialCount: 0,
      ...sessionData,
      createdAt: new Date(),
      updatedAt: new Date(),
    });
    return { success: true, id: sessionRef.id };
  } catch (error) {
    return { success: false, error: error.message };
  }
};

export const recordTrial = async (sessionId, trialData) => {
  try {
    const trialRef = await addDoc(
      collection(db, 'sessions', sessionId, 'trials'),
      {
        ...trialData,
        timestamp: new Date(),
      }
    );
    
    await updateDoc(doc(db, 'sessions', sessionId), { trialCount: increment(1), updatedAt: new Date() });

    return { success: true, id: trialRef.id };
  } catch (error) {
    return { success: false, error: error.message };
  }
};

export const endSession = async (sessionId) => {
  try {
    await updateDoc(doc(db, 'sessions', sessionId), {
      status: 'completed',
      endTime: new Date(),
      updatedAt: new Date(),
    });
    return { success: true };
  } catch (error) {
    return { success: false, error: error.message };
  }
};

// ============== PRE-MADE SETS ==============

export const getPreMadeSets = async (category = null, limit_ = 50) => {
  try {
    let q;
    if (category) {
      q = query(
        collection(db, 'preMadeSets'),
        where('category', '==', category),
        limit(limit_)
      );
    } else {
      q = query(
        collection(db, 'preMadeSets'),
        limit(limit_)
      );
    }
    
    const querySnapshot = await getDocs(q);
    const sets = querySnapshot.docs.map((doc) => ({ id: doc.id, ...doc.data() }));
    return { success: true, data: sets };
  } catch (error) {
    return { success: false, error: error.message };
  }
};

export const getPreMadeSet = async (setId) => {
  try {
    const setDoc_ = await getDoc(doc(db, 'preMadeSets', setId));
    if (setDoc_.exists()) {
      return { success: true, data: { id: setId, ...setDoc_.data() } };
    }
    return { success: false, error: 'Set not found' };
  } catch (error) {
    return { success: false, error: error.message };
  }
};

// ============== SAVED DECKS ==============

export const saveDeck = async (userId, deckName) => {
  try {
    await updateDoc(doc(db, 'users', userId), {
      savedDecks: arrayUnion(deckName),
    });
    return { success: true };
  } catch (error) {
    return { success: false, error: error.message };
  }
};

export const unsaveDeck = async (userId, deckName) => {
  try {
    await updateDoc(doc(db, 'users', userId), {
      savedDecks: arrayRemove(deckName),
    });
    return { success: true };
  } catch (error) {
    return { success: false, error: error.message };
  }
};

export const getSavedDecks = async (userId) => {
  try {
    const userDoc = await getDoc(doc(db, 'users', userId));
    if (userDoc.exists()) {
      return { success: true, data: userDoc.data().savedDecks || [] };
    }
    return { success: true, data: [] };
  } catch (error) {
    return { success: false, error: error.message };
  }
};

// ============== COVER IMAGE UPLOAD ==============

export const uploadCoverImage = async (userId, deckId, imageFile, onProgress) => {
  try {
    const fileName = `${Date.now()}_${imageFile.name}`;
    const storageRef = ref(storage, `users/${userId}/deck-covers/${deckId}/${fileName}`);
    await new Promise((resolve, reject) => {
      const task = uploadBytesResumable(storageRef, imageFile);
      task.on('state_changed',
        (snap) => onProgress && onProgress(Math.round((snap.bytesTransferred / snap.totalBytes) * 100)),
        reject,
        resolve,
      );
    });
    const downloadURL = await getDownloadURL(storageRef);
    return { success: true, url: downloadURL, path: storageRef.fullPath };
  } catch (error) {
    return { success: false, error: error.message };
  }
};

// ============== CUSTOM DECKS (Deck Builder) ==============

export const createCustomDeck = async (userId, deckData) => {
  try {
    const deckRef = await addDoc(collection(db, 'customDecks'), {
      userId,
      ...deckData,
      createdAt: new Date(),
      updatedAt: new Date(),
    });
    return { success: true, id: deckRef.id };
  } catch (error) {
    return { success: false, error: error.message };
  }
};

export const addCardToCustomDeck = async (deckId, cardData) => {
  try {
    await addDoc(collection(db, 'customDecks', deckId, 'cards'), {
      ...cardData,
      createdAt: new Date(),
    });
    return { success: true };
  } catch (error) {
    return { success: false, error: error.message };
  }
};

export const updateCustomDeck = async (deckId, updates) => {
  try {
    await updateDoc(doc(db, 'customDecks', deckId), {
      ...updates,
      updatedAt: new Date(),
    });
    return { success: true };
  } catch (error) {
    return { success: false, error: error.message };
  }
};

export const deleteCustomDeck = async (deckId) => {
  try {
    const [deckSnap, cardsSnap] = await Promise.all([
      getDoc(doc(db, 'customDecks', deckId)),
      getDocs(collection(db, 'customDecks', deckId, 'cards')),
    ]);
    await Promise.all([
      ...cardsSnap.docs.map(d => deleteDoc(d.ref)),
      ...cardsSnap.docs.map(d => deleteStorageUrlIfOwned(d.data().image)),
      deleteStorageUrlIfOwned(deckSnap.data()?.coverImage),
    ]);
    await deleteDoc(doc(db, 'customDecks', deckId));
    return { success: true };
  } catch (error) {
    return { success: false, error: error.message };
  }
};

export const clearCustomDeckCards = async (deckId) => {
  try {
    const cardsSnap = await getDocs(collection(db, 'customDecks', deckId, 'cards'));
    await Promise.all([
      ...cardsSnap.docs.map(d => deleteDoc(d.ref)),
      ...cardsSnap.docs.map(d => deleteStorageUrlIfOwned(d.data().image)),
    ]);
    return { success: true };
  } catch (error) {
    return { success: false, error: error.message };
  }
};

export const getUserCustomDecks = async (userId) => {
  try {
    const q = query(
      collection(db, 'customDecks'),
      where('userId', '==', userId),
      orderBy('updatedAt', 'desc')
    );
    const snap = await getDocs(q);
    return { success: true, data: snap.docs.map(d => ({ id: d.id, ...d.data() })) };
  } catch (error) {
    return { success: false, data: [], error: error.message };
  }
};

// Fetch a custom deck by title for use in SessionPage.
// Returns { success, deck } where deck matches the shape of DECKS items.
export const getCustomDeckForSession = async (userId, title) => {
  try {
    const q = query(
      collection(db, 'customDecks'),
      where('userId', '==', userId),
      where('title', '==', title)
    );
    const snap = await getDocs(q);
    if (snap.empty) return { success: false };

    const deckDoc = snap.docs[0];
    const deckData = deckDoc.data();

    const cardsSnap = await getDocs(
      collection(db, 'customDecks', deckDoc.id, 'cards')
    );
    const items = cardsSnap.docs
      .map(d => ({ ...d.data() }))
      .sort((a, b) => (a.order ?? 0) - (b.order ?? 0))
      .map(card => ({
        label: card.label,
        emoji: card.emoji || '🃏',
        image: card.image || null,
        imageBackup: null,
        variations: card.variations || [],
      }));

    return {
      success: true,
      deck: {
        name: deckData.title,
        emoji: deckData.emoji || '🃏',
        color: deckData.color || '#E4E4E4',
        coverImage: deckData.coverImage || null,
        isCustom: true,
        items,
      },
    };
  } catch (error) {
    return { success: false, error: error.message };
  }
};

export const getCustomDeckById = async (deckId) => {
  try {
    const deckSnap = await getDoc(doc(db, 'customDecks', deckId));
    if (!deckSnap.exists()) return { success: false };
    const deckData = deckSnap.data();

    const cardsSnap = await getDocs(collection(db, 'customDecks', deckId, 'cards'));
    const items = cardsSnap.docs
      .map(d => ({ ...d.data() }))
      .sort((a, b) => (a.order ?? 0) - (b.order ?? 0))
      .map(card => ({
        label: card.label,
        emoji: card.emoji || '🃏',
        image: card.image || null,
        imageBackup: null,
        variations: card.variations || [],
      }));

    return {
      success: true,
      deck: {
        name: deckData.title,
        emoji: deckData.emoji || '🃏',
        color: deckData.color || '#E4E4E4',
        coverImage: deckData.coverImage || null,
        isCustom: true,
        items,
      },
    };
  } catch (error) {
    return { success: false, error: error.message };
  }
};

// ============== VARIATION SELECTIONS ==============

export const saveVariationSelections = async (
  userId, deckName, itemLabel, selectedIndexes,
  removedIds = [], customVariations = []
) => {
  try {
    const id = `${userId}_${deckName}_${itemLabel}`.replace(/[^a-zA-Z0-9_-]/g, '_');
    await setDoc(doc(db, 'variationSelections', id), {
      userId, deckName, itemLabel,
      selectedIndexes,
      removedIds,
      customVariations,
      savedAt: new Date(),
    });
    return { success: true };
  } catch (error) {
    return { success: false, error: error.message };
  }
};

export const getVariationSelections = async (userId, deckName, itemLabel) => {
  try {
    const id = `${userId}_${deckName}_${itemLabel}`.replace(/[^a-zA-Z0-9_-]/g, '_');
    const snap = await getDoc(doc(db, 'variationSelections', id));
    if (snap.exists()) return { success: true, data: snap.data() };
    return { success: true, data: null };
  } catch (error) {
    return { success: false, error: error.message };
  }
};

// ============== MATCHING VARIATION SELECTIONS ==============
// Same pattern as VARIATION SELECTIONS above, but keyed by matching activity
// id + trial id instead of static deck name + card label (matching activities
// live in Firestore, not in the static DECKS array).

export const saveMatchingVariationSelections = async (userId, matchDeckId, trialId, activeMedia) => {
  try {
    const id = `${userId}_${matchDeckId}_${trialId}`.replace(/[^a-zA-Z0-9_-]/g, '_');
    await setDoc(doc(db, 'matchingVariationSelections', id), {
      userId, matchDeckId, trialId,
      activeMedia,
      savedAt: new Date(),
    });
    return { success: true };
  } catch (error) {
    return { success: false, error: error.message };
  }
};

export const getMatchingVariationSelections = async (userId, matchDeckId, trialId) => {
  try {
    const id = `${userId}_${matchDeckId}_${trialId}`.replace(/[^a-zA-Z0-9_-]/g, '_');
    const snap = await getDoc(doc(db, 'matchingVariationSelections', id));
    if (snap.exists()) return { success: true, data: snap.data() };
    return { success: true, data: null };
  } catch (error) {
    return { success: false, error: error.message };
  }
};

// ============== COMMUNITY DECKS ==============

export const shareCustomSet = async (userId, setId, setData) => {
  try {
    await setDoc(doc(db, 'communityDecks', setId), {
      ...setData,
      userId,
      sharedAt: new Date(),
      saves: 0,
    });
    return { success: true };
  } catch (error) {
    return { success: false, error: error.message };
  }
};

export const getCommunityDecks = async (sortBy = 'sharedAt') => {
  try {
    const q = query(
      collection(db, 'communityDecks'),
      orderBy(sortBy, 'desc'),
      limit(50)
    );
    const snapshot = await getDocs(q);
    const decks = snapshot.docs.map((d) => ({ id: d.id, ...d.data() }));
    return { success: true, data: decks };
  } catch (error) {
    return { success: false, data: [], error: error.message };
  }
};

// ============== USER FEEDBACK ==============

export const submitFeedback = async (userId, userEmail, rating, note, type = 'daily') => {
  try {
    await addDoc(collection(db, 'userFeedback'), {
      userId,
      userEmail: userEmail || '',
      rating,
      note: note.trim(),
      type,
      submittedAt: new Date(),
    });
    return { success: true };
  } catch (error) {
    return { success: false, error: error.message };
  }
};

// ============== SESSION RESULTS ==============

export const saveSessionResult = async (userId, deckName, sessionData) => {
  try {
    await addDoc(collection(db, 'sessionResults'), {
      userId,
      deckName,
      ...sessionData,
      completedAt: new Date(),
    });
    return { success: true };
  } catch (error) {
    return { success: false, error: error.message };
  }
};

export const getSessionLogs = async (userId, limit_ = 100) => {
  try {
    const q = query(
      collection(db, 'sessionResults'),
      where('userId', '==', userId),
      orderBy('completedAt', 'desc'),
      limit(limit_)
    );
    const snap = await getDocs(q);
    return { success: true, data: snap.docs.map(d => ({ id: d.id, ...d.data() })) };
  } catch (error) {
    return { success: false, data: [], error: error.message };
  }
};

export const getRecentSessionResults = async (userId, deckName, limit_ = 3) => {
  try {
    const q = query(
      collection(db, 'sessionResults'),
      where('userId', '==', userId),
      where('deckName', '==', deckName),
      orderBy('completedAt', 'desc'),
      limit(limit_)
    );
    const snap = await getDocs(q);
    return { success: true, data: snap.docs.map((d) => ({ id: d.id, ...d.data() })) };
  } catch (error) {
    return { success: false, data: [], error: error.message };
  }
};

// ============== DECK CARD ADDITIONS ==============

export const addCardToDeck = async (userId, deckName, cardData) => {
  try {
    const ref = await addDoc(
      collection(db, 'users', userId, 'deckAdditions', deckName, 'cards'),
      { ...cardData, createdAt: new Date() }
    );
    return { success: true, id: ref.id };
  } catch (error) {
    return { success: false, error: error.message };
  }
};

export const updateCardInDeck = async (userId, deckName, cardId, cardData) => {
  try {
    const docRef = doc(db, 'users', userId, 'deckAdditions', deckName, 'cards', cardId);
    await updateDoc(docRef, { ...cardData, updatedAt: new Date() });
    return { success: true };
  } catch (error) {
    return { success: false, error: error.message };
  }
};

export const getDeckAdditions = async (userId, deckName) => {
  try {
    const snap = await getDocs(
      collection(db, 'users', userId, 'deckAdditions', deckName, 'cards')
    );
    return {
      success: true,
      data: snap.docs.map(d => {
        const data = d.data();
        return { id: d.id, ...data, variations: data.variations || [] };
      }),
    };
  } catch (error) {
    return { success: false, data: [] };
  }
};

export const uploadDeckCardImage = async (userId, deckName, cardId, imageFile, onProgress) => {
  try {
    const fileName = `${Date.now()}_${imageFile.name}`;
    const storageRef = ref(storage, `users/${userId}/deckAdditions/${deckName}/${cardId}/${fileName}`);
    await new Promise((resolve, reject) => {
      const task = uploadBytesResumable(storageRef, imageFile);
      task.on('state_changed',
        (snap) => onProgress && onProgress(Math.round((snap.bytesTransferred / snap.totalBytes) * 100)),
        reject,
        resolve,
      );
    });
    const downloadURL = await getDownloadURL(storageRef);
    return { success: true, url: downloadURL };
  } catch (error) {
    return { success: false, error: error.message };
  }
};

// ============== USER SEQUENCING DECKS (customer-created activities) ==============

export const uploadSequencingStepImage = async (userId, deckId, stepId, imageFile, onProgress) => {
  try {
    const fileName = `${Date.now()}_${imageFile.name}`;
    const storageRef = ref(storage, `users/${userId}/uploads/sequencing/${deckId}/${stepId}/${fileName}`);
    await new Promise((resolve, reject) => {
      const task = uploadBytesResumable(storageRef, imageFile);
      task.on('state_changed',
        (snap) => onProgress && onProgress(Math.round((snap.bytesTransferred / snap.totalBytes) * 100)),
        reject,
        resolve,
      );
    });
    const downloadURL = await getDownloadURL(storageRef);
    return { success: true, url: downloadURL, path: storageRef.fullPath };
  } catch (error) {
    return { success: false, error: error.message };
  }
};

export const createUserSequencingDeck = async (userId, deckData) => {
  try {
    const deckRef = await addDoc(collection(db, 'userSequencingDecks'), {
      userId,
      ...deckData,
      createdAt: new Date(),
      updatedAt: new Date(),
    });
    return { success: true, id: deckRef.id };
  } catch (error) {
    return { success: false, error: error.message };
  }
};

export const updateUserSequencingDeck = async (deckId, updates) => {
  try {
    await updateDoc(doc(db, 'userSequencingDecks', deckId), {
      ...updates,
      updatedAt: new Date(),
    });
    return { success: true };
  } catch (error) {
    return { success: false, error: error.message };
  }
};

export const getUserSequencingDecks = async (userId) => {
  try {
    const q = query(
      collection(db, 'userSequencingDecks'),
      where('userId', '==', userId),
      orderBy('updatedAt', 'desc')
    );
    const snap = await getDocs(q);
    return { success: true, data: snap.docs.map(d => ({ id: d.id, ...d.data() })) };
  } catch (error) {
    return { success: false, data: [], error: error.message };
  }
};

export const getUserSequencingDeckById = async (deckId) => {
  try {
    const deckSnap = await getDoc(doc(db, 'userSequencingDecks', deckId));
    if (!deckSnap.exists()) return { success: false };
    return { success: true, deck: { id: deckSnap.id, ...deckSnap.data() } };
  } catch (error) {
    return { success: false, error: error.message };
  }
};

export const deleteUserSequencingDeck = async (deckId) => {
  try {
    const deckSnap = await getDoc(doc(db, 'userSequencingDecks', deckId));
    const steps = deckSnap.data()?.steps || [];
    await Promise.all(steps.map(s => deleteStorageUrlIfOwned(s.imageUrl)));
    await deleteDoc(doc(db, 'userSequencingDecks', deckId));
    return { success: true };
  } catch (error) {
    return { success: false, error: error.message };
  }
};

// ============== USER MATCHING DECKS (customer-created, own stimuli) ==============

export const uploadMatchingCardImage = async (userId, deckId, cardId, imageFile, onProgress) => {
  try {
    const fileName = `${Date.now()}_${imageFile.name}`;
    const storageRef = ref(storage, `users/${userId}/uploads/matching/${deckId}/${cardId}/${fileName}`);
    await new Promise((resolve, reject) => {
      const task = uploadBytesResumable(storageRef, imageFile);
      task.on('state_changed',
        (snap) => onProgress && onProgress(Math.round((snap.bytesTransferred / snap.totalBytes) * 100)),
        reject,
        resolve,
      );
    });
    const downloadURL = await getDownloadURL(storageRef);
    return { success: true, url: downloadURL, path: storageRef.fullPath };
  } catch (error) {
    return { success: false, error: error.message };
  }
};

export const createUserMatchingDeck = async (userId, deckData) => {
  try {
    const deckRef = await addDoc(collection(db, 'userMatchingDecks'), {
      userId,
      ...deckData,
      createdAt: new Date(),
      updatedAt: new Date(),
    });
    return { success: true, id: deckRef.id };
  } catch (error) {
    return { success: false, error: error.message };
  }
};

export const updateUserMatchingDeck = async (deckId, updates) => {
  try {
    await updateDoc(doc(db, 'userMatchingDecks', deckId), {
      ...updates,
      updatedAt: new Date(),
    });
    return { success: true };
  } catch (error) {
    return { success: false, error: error.message };
  }
};

export const getUserMatchingDecks = async (userId) => {
  try {
    const q = query(
      collection(db, 'userMatchingDecks'),
      where('userId', '==', userId),
      orderBy('updatedAt', 'desc')
    );
    const snap = await getDocs(q);
    return { success: true, data: snap.docs.map(d => ({ id: d.id, ...d.data() })) };
  } catch (error) {
    return { success: false, data: [], error: error.message };
  }
};

export const getUserMatchingDeckById = async (deckId) => {
  try {
    const deckSnap = await getDoc(doc(db, 'userMatchingDecks', deckId));
    if (!deckSnap.exists()) return { success: false };
    return { success: true, deck: { id: deckSnap.id, ...deckSnap.data() } };
  } catch (error) {
    return { success: false, error: error.message };
  }
};

export const deleteUserMatchingDeck = async (deckId) => {
  try {
    const deckSnap = await getDoc(doc(db, 'userMatchingDecks', deckId));
    const trials = deckSnap.data()?.trials || [];
    await Promise.all(trials.flatMap(t => [
      deleteStorageUrlIfOwned(t.prompt?.imageUrl),
      ...(t.choices || []).map(c => deleteStorageUrlIfOwned(c.imageUrl)),
    ]));
    await deleteDoc(doc(db, 'userMatchingDecks', deckId));
    return { success: true };
  } catch (error) {
    return { success: false, error: error.message };
  }
};

